/**
 * Query-key factory.
 *
 * Centralizing keys here lets us:
 *   1. Invalidate cleanly (e.g. `queryClient.invalidateQueries({ queryKey: keys.batches.all })`).
 *   2. Avoid string-typo cache misses.
 *   3. See at a glance which keys exist and how they nest.
 *
 * Convention: hierarchical, narrowest-last. `keys.batches.detail(id)`
 * is a strict descendant of `keys.batches.all` so invalidating "all
 * batch data" also clears every per-batch detail/issue/audit cache.
 */

export const queryKeys = {
  currentUser: ["currentUser"] as const,
  batches: {
    all: ["batches"] as const,
    list: () => [...queryKeys.batches.all, "list"] as const,
    detail: (batchId: string | undefined) => [...queryKeys.batches.all, "detail", batchId] as const,
    issues: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "issues"] as const,
    audit: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "audit"] as const,
    reports: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "reports"] as const,
    corrections: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "corrections"] as const,
    calcResults: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "calc-results"] as const,
    substitutionMeter: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "substitution-meter"] as const,
    workbook: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "workbook-comparison"] as const,
    calibrationReadiness: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "calibration-readiness"] as const,
    validationStatus: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "validation-status"] as const,
    // A descendant of detail(batchId), NOT a sibling: a regenerated draft reuses
    // its report_id, and invalidateBatchData() only clears the detail prefix — a
    // sibling key would keep serving pre-regeneration cells for its whole
    // staleTime, i.e. reviewing obsolete figures.
    reportPreview: (
      batchId: string | undefined,
      reportId: string | undefined,
      sheet: string | undefined,
    ) =>
      [
        ...queryKeys.batches.detail(batchId),
        "report-preview",
        reportId,
        // `null` for "no sheet requested" (the BE picks the active one) — a
        // string sentinel would collide with a workbook that has a sheet of that
        // name, serving the active sheet's cells when the user picks it.
        sheet ?? null,
      ] as const,
    // Backfill has no GET endpoint — we cache the latest mutation response under
    // this key so the controls reflect state within a session. A descendant of
    // detail(batchId) so it's cleared with the rest of the batch surface.
    backfill: (batchId: string | undefined) =>
      [...queryKeys.batches.all, "detail", batchId, "backfill"] as const,
  },
  config: {
    all: ["config"] as const,
    active: (profileKey: string | undefined, at?: string) =>
      [...queryKeys.config.all, "active", profileKey ?? "none", at ?? "now"] as const,
    versions: (profileKey: string | undefined) =>
      [...queryKeys.config.all, "versions", profileKey ?? "none"] as const,
    version: (versionId: string | undefined) =>
      [...queryKeys.config.all, "version", versionId] as const,
    diff: (fromId: string | undefined, toId: string | undefined) =>
      [...queryKeys.config.all, "diff", fromId ?? "none", toId ?? "none"] as const,
  },
  uploads: {
    all: ["uploads"] as const,
    list: (period?: string) => [...queryKeys.uploads.all, "list", period ?? "all"] as const,
    detail: (uploadId: string | undefined) =>
      [...queryKeys.uploads.all, "detail", uploadId] as const,
    rows: (uploadId: string | undefined, limit: number, offset: number) =>
      [...queryKeys.uploads.all, "detail", uploadId, "rows", limit, offset] as const,
  },
  // Notifications — topic catalog + caller subscriptions.
  notifications: {
    all: ["notifications"] as const,
    topics: () => [...queryKeys.notifications.all, "topics"] as const,
    subscriptions: () => [...queryKeys.notifications.all, "subscriptions"] as const,
  },
  // Quarterly reports (RFS4800) — keyed by year + quarter.
  quarters: {
    all: ["quarters"] as const,
    reports: (year: number | undefined, quarter: number | undefined) =>
      [...queryKeys.quarters.all, year ?? "none", quarter ?? "none", "reports"] as const,
  },
  // Annual reports (RFS0902) — keyed by year.
  years: {
    all: ["years"] as const,
    reports: (year: number | undefined) =>
      [...queryKeys.years.all, year ?? "none", "reports"] as const,
  },
  // LCFS reporting surface — the two registries plus the allocation, which is
  // period-scoped where the rosters are current-state.
  lcfs: {
    all: ["lcfs"] as const,
    fse: () => [...queryKeys.lcfs.all, "fse"] as const,
    dairies: () => [...queryKeys.lcfs.all, "dairies"] as const,
    /**
     * The allocation's stored inputs, built into the whole history once.
     *
     * Not keyed on a period: every period is allocated from the same four
     * lists, because a period's opening balance is the previous period's close.
     * One cache entry means switching periods is a re-render, not a refetch.
     */
    inputs: () => [...queryKeys.lcfs.all, "inputs"] as const,
    /**
     * A period's support verdict and the statements behind it.
     *
     * Keyed per period, unlike `inputs`: support is a fact about one month and
     * filing a statement for July must not invalidate June's answer.
     */
    validation: (period: string) => [...queryKeys.lcfs.all, "validation", period] as const,
    statements: (period: string) => [...queryKeys.lcfs.all, "statements", period] as const,
    /**
     * Not period-keyed: a confirmation states every month of the contract term,
     * so the list is one fact about the organization rather than about a month.
     */
    confirmations: () => [...queryKeys.lcfs.all, "confirmations"] as const,
  },
  // One month's preview of a dropped allocation workbook. Its own root rather
  // than under `lcfs`, so invalidating the ledger never re-posts a file the
  // operator has already moved on from. `fileKey` changes on every drop.
  lcfsAllocationPreview: (fileKey: number, period: string) =>
    ["lcfs-allocation-preview", fileKey, period] as const,
  // Data-visualization surface. `series` is keyed on every query param so the
  // browser dedupes identical (role, facility, window) fetches across the
  // explorer and the batch Data tab, and historical windows cache forever.
  data: {
    all: ["data"] as const,
    sources: () => [...queryKeys.data.all, "sources"] as const,
    // Prefix over every (role, facility, window) series query — used to
    // invalidate cached series (incl. the cache-forever historical windows)
    // when source data changes via accept/correction/backfill.
    seriesAll: () => [...queryKeys.data.all, "series"] as const,
    series: (
      role: string,
      facility: string | null | undefined,
      start: string,
      end: string,
      granularity = "daily",
    ) =>
      [...queryKeys.data.all, "series", role, facility ?? "none", start, end, granularity] as const,
    // Window-scoped corrections overlay (GET /data/corrections) — the prefix is
    // invalidated whenever a correction is proposed/decided (invalidateBatchData).
    correctionsAll: () => [...queryKeys.data.all, "corrections"] as const,
    corrections: (start: string, end: string) =>
      [...queryKeys.data.all, "corrections", start, end] as const,
  },
} as const;
